import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Stack,
  IconButton,
  InputAdornment,
  TextField,
  Container,
  Alert,
  Box,
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Icon } from "@iconify/react";
import { useFormik } from "formik";
import { supabase } from "../../utils/API";

export default function UpdatePassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const tokenHash = searchParams.get("token_hash");
  const otpType = searchParams.get("type");
  const urlError = searchParams.get("error");
  const urlErrorCode = searchParams.get("error_code");
  const urlErrorDescription = searchParams.get("error_description");

  const [isChecking, setIsChecking] = useState(true);
  const [hasSession, setHasSession] = useState(false);
  const [sessionError, setSessionError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const exchangeAndVerify = async () => {
      try {
        setIsChecking(true);

        // 0. If Supabase redirected directly with an error (e.g. otp_expired)
        if (urlError || urlErrorCode || urlErrorDescription) {
          if (isMounted) {
            if (urlErrorCode === "otp_expired") {
              setSessionError("This password reset link has expired. Please request a new reset link from the mobile app.");
            } else {
              setSessionError(urlErrorDescription || "Password reset link is invalid or has expired. Please request a new link.");
            }
          }
          return;
        }

        // 1. If Token Hash is present (Supabase Email Template using {{ .TokenHash }})
        if (tokenHash) {
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: (otpType as any) || "recovery",
          });

          if (error) {
            if (isMounted) {
              setSessionError(
                error.message || "This password reset token is invalid or has expired. Please request a new link."
              );
            }
            return;
          }

          if (isMounted) {
            setHasSession(!!data.session);
          }
          return;
        }

        // 2. If recovery code is present in query string (?code=...)
        if (code) {
          // Check if this recovery code is valid in the database
          const { data: isValid, error: valErr } = await supabase.rpc(
            "validate_recovery_code",
            { p_code: code }
          );

          if (isValid) {
            if (isMounted) {
              setHasSession(true);
            }
            return;
          }

          // Fallback: Check if PKCE exchange works (in case verifier is in localStorage)
          try {
            const { data, error } = await supabase.auth.exchangeCodeForSession(code);
            if (!error && data.session) {
              if (isMounted) {
                setHasSession(true);
              }
              return;
            }
          } catch (_) {}

          // Fallback: Check if session is already active
          const { data: sessionData } = await supabase.auth.getSession();
          if (isMounted) {
            if (sessionData.session) {
              setHasSession(true);
            } else {
              setSessionError(
                "This password reset link is invalid or has expired. Please request a new link from the mobile app."
              );
            }
          }
          return;
        }

        // 3. Fallback: Check if an active session or hash token is already available (#access_token=...)
        const { data: sessionData } = await supabase.auth.getSession();
        if (isMounted) {
          if (sessionData.session) {
            setHasSession(true);
          } else {
            setSessionError(
              "No reset authorization code found. Please open the link directly from your reset email."
            );
          }
        }
      } catch (err: any) {
        if (isMounted) {
          setSessionError(
            err.message || "Failed to authenticate password reset session."
          );
        }
      } finally {
        if (isMounted) {
          setIsChecking(false);
        }
      }
    };

    exchangeAndVerify();

    return () => {
      isMounted = false;
    };
  }, [code, tokenHash, otpType]);

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validate: (values) => {
      const errors: any = {};
      if (!values.password) {
        errors.password = "Password is required";
      } else if (values.password.length < 8) {
        errors.password = "Password must be at least 8 characters";
      }

      if (!values.confirmPassword) {
        errors.confirmPassword = "Confirm password is required";
      } else if (values.confirmPassword !== values.password) {
        errors.confirmPassword = "Passwords do not match";
      }
      return errors;
    },
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);
        setSubmitError("");

        // 1. If we have a recovery code, update password directly via secure RPC
        if (code) {
          const { data: rpcRes, error: rpcErr } = await supabase.rpc(
            "reset_password_with_recovery_code",
            { p_code: code, p_new_password: values.password }
          );

          if (rpcErr || (rpcRes && !rpcRes.success)) {
            setSubmitError(
              rpcErr?.message || rpcRes?.error || "Failed to update password."
            );
            return;
          }

          setIsSuccess(true);
          return;
        }

        // 2. Otherwise update password using active session
        const { data, error } = await supabase.auth.updateUser({
          password: values.password,
        });

        if (error) {
          setSubmitError(error.message);
        } else if (data.user) {
          setIsSuccess(true);
        }
      } catch (e: any) {
        setSubmitError("An error occurred while updating password. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #004D80 0%, #0072BC 100%)",
        p: 2,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
      }}
    >
      <Container maxWidth="xs">
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            padding: { xs: "28px 24px", sm: "36px 32px" },
            borderRadius: "6px",
            boxShadow: "0 12px 32px rgba(0, 0, 0, 0.2)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* Logo */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "16px",
            }}
          >
            <img
              src="/mtrcb-admin-logo.png"
              alt="MTRCB Admin Logo"
              style={{
                height: "68px",
                objectFit: "contain",
              }}
            />
          </Box>

          {!isSuccess && (
            <>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: "#0F172A",
                  textAlign: "center",
                  marginBottom: "6px",
                  fontSize: { xs: "20px", sm: "22px" },
                  letterSpacing: "-0.02em",
                }}
              >
                Reset Your Password
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "#64748B",
                  textAlign: "center",
                  marginBottom: "24px",
                  lineHeight: 1.5,
                  fontSize: "13px",
                }}
              >
                Enter your new password below to update your MTRCB account credentials.
              </Typography>
            </>
          )}

          {/* Loading Verification State */}
          {isChecking && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 4,
                gap: 2,
              }}
            >
              <CircularProgress size={32} sx={{ color: "#0072BC" }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: "13px" }}>
                Verifying password reset session...
              </Typography>
            </Box>
          )}

          {/* Session Error State */}
          {!isChecking && sessionError && (
            <Stack spacing={2} sx={{ width: "100%", mt: 1 }}>
              <Alert
                severity="error"
                sx={{
                  borderRadius: "4px",
                  fontSize: "13px",
                  "& .MuiAlert-message": { lineHeight: 1.4 },
                }}
              >
                {sessionError}
              </Alert>
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ fontSize: "12px", px: 1 }}>
                Please return to the MTRCB Mobile App to request a fresh password reset link.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate("/login")}
                sx={{
                  backgroundColor: "#0072BC",
                  "&:hover": { backgroundColor: "#005B96" },
                  borderRadius: "4px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "14px",
                  py: 1,
                  boxShadow: "none",
                }}
              >
                Back to Login
              </Button>
            </Stack>
          )}

          {/* Success State */}
          {!isChecking && isSuccess && (
            <Stack spacing={2.5} sx={{ width: "100%", alignItems: "center", py: 1 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  backgroundColor: "rgba(0, 147, 76, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon
                  icon="lets-icons:check-fill"
                  style={{ fontSize: "40px", color: "#00934C" }}
                />
              </Box>

              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    color: "#0F172A",
                    fontSize: { xs: "20px", sm: "22px" },
                    letterSpacing: "-0.02em",
                    mb: 1,
                  }}
                >
                  Password Updated!
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748B",
                    fontSize: "13.5px",
                    lineHeight: 1.55,
                  }}
                >
                  Your account password has been updated successfully.
                </Typography>
              </Box>

              <Box
                sx={{
                  width: "100%",
                  backgroundColor: "#F0FDF4",
                  border: "1px solid #BBF7D0",
                  borderRadius: "6px",
                  p: 2.5,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    color: "#166534",
                    fontWeight: 700,
                    fontSize: "14px",
                  }}
                >
                  <Icon icon="solar:smartphone-bold" style={{ fontSize: "20px" }} />
                  <span>Ready to Log In</span>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#15803D",
                    fontSize: "13px",
                    lineHeight: 1.5,
                  }}
                >
                  You can now open the <strong>MTRCB Mobile App</strong> and log in using your new password.
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#64748B",
                    fontSize: "12px",
                    mt: 0.5,
                  }}
                >
                  You may safely close this browser window.
                </Typography>
              </Box>
            </Stack>
          )}

          {/* Password Reset Form */}
          {!isChecking && !sessionError && !isSuccess && hasSession && (
            <form onSubmit={formik.handleSubmit} style={{ width: "100%" }}>
              <Stack spacing={2}>
                {submitError && (
                  <Alert severity="error" sx={{ borderRadius: "4px", fontSize: "13px" }}>
                    {submitError}
                  </Alert>
                )}

                <TextField
                  fullWidth
                  size="small"
                  name="password"
                  label="New Password"
                  type={showPassword ? "text" : "password"}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  InputProps={{
                    sx: { borderRadius: "4px", fontSize: "14px" },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                        >
                          <Icon
                            icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  size="small"
                  name="confirmPassword"
                  label="Confirm New Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.confirmPassword &&
                    Boolean(formik.errors.confirmPassword)
                  }
                  helperText={
                    formik.touched.confirmPassword &&
                    formik.errors.confirmPassword
                  }
                  InputProps={{
                    sx: { borderRadius: "4px", fontSize: "14px" },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          size="small"
                        >
                          <Icon
                            icon={
                              showConfirmPassword
                                ? "eva:eye-fill"
                                : "eva:eye-off-fill"
                            }
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <LoadingButton
                  fullWidth
                  type="submit"
                  variant="contained"
                  loading={formik.isSubmitting}
                  sx={{
                    backgroundColor: "#0072BC",
                    "&:hover": { backgroundColor: "#005B96" },
                    borderRadius: "4px",
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: 700,
                    py: 1.1,
                    mt: 1,
                    boxShadow: "none",
                  }}
                >
                  Update Password
                </LoadingButton>
              </Stack>
            </form>
          )}
        </Box>
      </Container>
    </Box>
  );
}
